import { useCallback, useEffect, useState } from 'react';
import { initialBalancesApi, type InitialBalanceRow, type InitialBalanceSummary } from '../lib/api/initial-balances';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';
import { SettingsImportModal } from './SettingsImportModal';

interface Props {
  onGoToAccountsTab?: () => void;
}

const TYPE_LABEL: Record<string, { label: string; group: 'blue' | 'green' | 'purple' | 'blue' }> = {
  asset: { label: 'Tài sản', group: 'blue' },
  liability: { label: 'Nợ phải trả', group: 'green' },
  equity: { label: 'Vốn CSH', group: 'purple' },
  revenue: { label: 'Doanh thu', group: 'green' },
  expense: { label: 'Chi phí', group: 'green' },
  other: { label: 'Khác', group: 'blue' },
};

const YEAR = 2026;

export function InitialBalanceSection({ onGoToAccountsTab }: Props = {}) {
  const { toast, showToast } = useToast();
  const [items, setItems] = useState<InitialBalanceRow[]>([]);
  const [summary, setSummary] = useState<InitialBalanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await initialBalancesApi.list(YEAR);
      setItems(r.items); setSummary(r.summary);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const locked = summary?.locked ?? false;

  const filtered = search.trim()
    ? items.filter((i) => i.accountCode.includes(search.trim()) || i.name.toLowerCase().includes(search.trim().toLowerCase()))
    : items;

  const grouped = Array.from(
    filtered.reduce((map, r) => {
      const key = r.type;
      (map.get(key) ?? map.set(key, []).get(key)!).push(r);
      return map;
    }, new Map<string, InitialBalanceRow[]>()).entries(),
  );

  const updateCell = async (row: InitialBalanceRow, field: 'debit' | 'credit', value: number) => {
    if (locked) return showToast('Số dư đã khoá — không sửa được. Bấm "Mở khoá" (KTT) trước.');
    setSaving(row.id);
    try {
      await initialBalancesApi.upsert(YEAR, row.accountCode, field === 'debit' ? value : row.debit, field === 'credit' ? value : row.credit);
      // Optimistic update local state
      setItems((prev) => prev.map((i) => i.accountCode === row.accountCode ? { ...i, [field]: value } : i));
      // Reload summary
      const r = await initialBalancesApi.list(YEAR);
      setSummary(r.summary);
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(null); }
  };

  const removeRow = async (row: InitialBalanceRow) => {
    if (locked) return;
    if (!window.confirm(`Bỏ TK ${row.accountCode} - ${row.name} khỏi bảng?`)) return;
    try {
      await initialBalancesApi.remove(row.id);
      showToast(`Đã bỏ TK ${row.accountCode}`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const handleLock = async () => {
    if (!summary?.balanced) {
      return showToast(`Không khoá được: Nợ ≠ Có (lệch ${formatVND(Math.abs((summary?.totalDebit ?? 0) - (summary?.totalCredit ?? 0)))})`);
    }
    if (!window.confirm(`Khoá số dư đầu kỳ ${YEAR}?\n\nTổng ${formatVND(summary.totalDebit)}. Sau khi khoá, không thể sửa.`)) return;
    try {
      const r = await initialBalancesApi.lock(YEAR);
      showToast(`Đã khoá ${r.lockedCount} TK`);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Mở khoá số dư đầu kỳ (yêu cầu quyền KTT)?')) return;
    try {
      await initialBalancesApi.unlock(YEAR);
      showToast('Đã mở khoá');
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const handleCarryForward = async () => {
    const prev = YEAR - 1;
    const hasExisting = items.length > 0;
    const overwrite = hasExisting
      ? window.confirm(
          `Năm ${YEAR} đã có ${items.length} TK khai báo số dư. Kết chuyển từ ${prev} sẽ:\n\n` +
          `• OK = GHI ĐÈ toàn bộ TK permanent (asset/liability/equity) bằng số dư ${prev}.\n` +
          `• Cancel = CHỈ TẠO MỚI TK chưa có, giữ nguyên TK đã khai.\n\n` +
          `TK loại revenue/expense và TK đã khoá được bỏ qua tự động.`,
        )
      : window.confirm(
          `Kết chuyển toàn bộ số dư permanent (asset/liability/equity) từ năm ${prev} sang ${YEAR}?\n\n` +
          `TK loại revenue/expense sẽ được bỏ qua (reset hằng năm).`,
        );
    // If prompt cancelled and no items, abort
    if (!hasExisting && !overwrite) return;
    try {
      const r = await initialBalancesApi.carryForward(prev, YEAR, overwrite);
      showToast(r.message);
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <>
      <div className="ibl-info">
        <div className="ibl-info-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg></div>
        <div>Danh sách TK phía dưới lấy từ <b>Hệ thống tài khoản</b> · Chỉ hiện TK đang bật · Muốn thêm TK mới phải setup ở tab <b>Hệ thống tài khoản</b> trước.</div>
        <button type="button" className="ibl-info-btn" onClick={() => onGoToAccountsTab ? onGoToAccountsTab() : showToast('Chuyển tab Hệ thống TK')}>↗ Đi tới Hệ thống TK</button>
      </div>

      {/* Header banner */}
      <div className="ibl-hero">
        <div className="ibl-hero-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
        </div>
        <div className="ibl-hero-meta">
          <div className="ibl-hero-title-row">
            <h2>Số dư đầu kỳ · 01/01/{YEAR}</h2>
            <span className={`ibl-status-pill ${locked ? 'locked' : 'draft'}`}>{locked ? 'Đã khoá' : 'Nháp · chưa khoá'}</span>
          </div>
          <p>Dùng cho toàn bộ Sổ cái + Sổ tiền mặt năm {YEAR}.</p>
        </div>
        <div className="ibl-hero-actions">
          <button type="button" className="pay-btn-outline" disabled={locked} onClick={() => setShowImport(true)}>Import Excel</button>
          <button type="button" className="pay-btn-outline" disabled={locked} onClick={handleCarryForward}>Kết chuyển từ {YEAR - 1}</button>
        </div>
      </div>

      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* Stat cards */}
      <div className="ibl-stat-grid">
        <div className="ibl-stat">
          <div className="ibl-stat-label">Tổng số dư Nợ</div>
          <div className="ibl-stat-value debit">{summary ? formatVND(summary.totalDebit) : '—'}</div>
        </div>
        <div className="ibl-stat">
          <div className="ibl-stat-label">Tổng số dư Có</div>
          <div className="ibl-stat-value credit">{summary ? formatVND(summary.totalCredit) : '—'}</div>
        </div>
        <div className={`ibl-stat ${summary?.balanced ? 'ok' : 'warn'}`}>
          <div className="ibl-stat-label">{summary?.balanced ? '✓ Cân đối' : 'Cần cân đối'}</div>
          <div className="ibl-stat-value">
            {summary?.balanced ? 'Nợ = Có' : summary ? `Lệch ${formatVND(Math.abs(summary.totalDebit - summary.totalCredit))}` : '—'}
          </div>
        </div>
        <div className="ibl-stat">
          <div className="ibl-stat-label">TK có số dư</div>
          <div className="ibl-stat-value">{summary?.nonZeroCount ?? '—'}<span className="ibl-stat-total">/{summary?.count ?? '—'} TK</span></div>
        </div>
      </div>

      {/* Table */}
      <div className="ibl-table-wrap">
        <div className="ibl-tbl-head">
          <div className="pay-search coa-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input placeholder="Tìm mã hoặc tên TK…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button type="button" className="pay-btn-dark ibl-add-btn" disabled={locked} onClick={() => showToast('TK đang bật từ Hệ thống tài khoản được tự động hiển thị tại đây.')}>
            + Thêm TK vào bảng
          </button>
        </div>

        <div className="ibl-table-scroll">{loading ? <div className="pay-empty">Đang tải…</div> : (
          <table className="ibl-tbl">
            <thead>
              <tr>
                <th className="col-code">Mã TK</th>
                <th className="col-name">Tên tài khoản</th>
                <th className="col-amt">Số dư Nợ (đ)</th>
                <th className="col-amt">Số dư Có (đ)</th>
                <th className="col-act" />
              </tr>
            </thead>
            <tbody>
              {grouped.map(([type, rows]) => (
                <>
                  <tr key={type} className={`ibl-group-row ibl-group-${TYPE_LABEL[type]?.group ?? 'blue'}`}>
                    <td colSpan={5}>Loại · {TYPE_LABEL[type]?.label ?? type}</td>
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.id} className="ibl-row">
                      <td className="col-code"><span className="coa-code-pill">{r.accountCode}</span></td>
                      <td className="col-name">{r.name}</td>
                      <td className="col-amt">
                        <NumInput
                          value={r.debit}
                          disabled={locked || saving === r.id}
                          onCommit={(v) => v !== r.debit && updateCell(r, 'debit', v)}
                        />
                      </td>
                      <td className="col-amt">
                        <NumInput
                          value={r.credit}
                          disabled={locked || saving === r.id}
                          onCommit={(v) => v !== r.credit && updateCell(r, 'credit', v)}
                        />
                      </td>
                      <td className="col-act">
                        {!r.id.startsWith('coa:') && <button type="button" className="coa-icon-btn danger" title="Xoá" disabled={locked} onClick={() => removeRow(r)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 0 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                        </button>}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
              {summary && (
                <tr className="ibl-total-row">
                  <td colSpan={2}>TỔNG CỘNG</td>
                  <td className="col-amt debit">{formatVND(summary.totalDebit)}</td>
                  <td className="col-amt credit">{formatVND(summary.totalCredit)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        )}</div>
      </div>

      {/* Action bar */}
      <div className="ibl-actionbar">
        <div className="ibl-actionbar-hint">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
          Sau khi <b>Khoá số dư</b>, không thể sửa. Chỉ KTT được mở lại.
        </div>
        <div className="ibl-actionbar-btns">
          {locked ? (
            <button type="button" className="pay-btn-outline" onClick={handleUnlock}>Mở khoá</button>
          ) : (
            <>
              <button type="button" className="pay-btn-outline" onClick={() => load()}>Tải lại</button>
              <button type="button" className="pay-btn-dark" onClick={handleLock}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                Khoá số dư đầu kỳ
              </button>
            </>
          )}
        </div>
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
      <SettingsImportModal kind="balance" year={YEAR} open={showImport} onClose={() => setShowImport(false)} onDone={load} />
    </>
  );
}

function NumInput({ value, disabled, onCommit }: { value: number; disabled?: boolean; onCommit: (v: number) => void }) {
  const [text, setText] = useState(value ? value.toLocaleString('vi-VN') : '');
  useEffect(() => { setText(value ? value.toLocaleString('vi-VN') : ''); }, [value]);
  return (
    <input
      className="ibl-num"
      inputMode="numeric"
      value={text}
      disabled={disabled}
      onChange={(e) => {
        const raw = e.target.value.replace(/\D/g, '');
        setText(raw ? Number(raw).toLocaleString('vi-VN') : '');
      }}
      onBlur={() => onCommit(Number(text.replace(/\D/g, '')) || 0)}
      placeholder="—"
    />
  );
}
